if (!customElements.get('bewo-poltrona-stories')) {
  class BewoPoltronaStories extends HTMLElement {
    constructor() {
      super();
      this.activePage = 0;
      this.pageSize = 3;
      this.mobileQuery = window.matchMedia('(max-width: 767.98px)');
      this.mobileBound = false;
      this.swipe = {
        active: false,
        startX: 0,
        startY: 0,
        deltaX: 0,
        locked: false,
        originOffset: 0,
      };
    }

    connectedCallback() {
      this.scroller = this.querySelector('.bewo-poltrona-stories__scroller');
      this.track = this.querySelector('.bewo-poltrona-stories__items');
      this.pillsWrap = this.querySelector('.bewo-poltrona-stories__pills');
      this.items = Array.from(this.querySelectorAll('.bewo-poltrona-stories__item'));
      this.pills = Array.from(this.querySelectorAll('.bewo-poltrona-stories__pill'));
      this.triggers = Array.from(this.querySelectorAll('.bewo-poltrona-stories__trigger'));
      this.sectionEl = this.closest('.shopify-section');

      if (this._storiesBound) {
        this.unbindAll();
      }

      if (!this._handlersBound) {
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onPillClick = this.onPillClick.bind(this);
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onSwipeStart = this.onSwipeStart.bind(this);
        this.onSwipeMove = this.onSwipeMove.bind(this);
        this.onSwipeEnd = this.onSwipeEnd.bind(this);
        this._handlersBound = true;
      }

      this.bindAll();
      this._storiesBound = true;

      this.mobileQuery.addEventListener('change', this.onBreakpointChange);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.syncMobileCarousel();
      this.cleanupOrphanModals({ keepInSection: true });
    }

    bindAll() {
      this.pills.forEach((pill) => {
        pill.addEventListener('click', this.onPillClick);
      });

      // Só click — sem pointer/touch nos botões (evita conflito com carrossel no iOS).
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.onTriggerClick, true);
      });
    }

    unbindAll() {
      this.pills?.forEach((pill) => {
        pill.removeEventListener('click', this.onPillClick);
      });
      this.triggers?.forEach((trigger) => {
        trigger.removeEventListener('click', this.onTriggerClick, true);
      });
      this.unbindSwipe();
    }

    disconnectedCallback() {
      if (!this._storiesBound) return;
      this._storiesBound = false;

      this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
      window.removeEventListener('resize', this.onResize);
      this.disableMobileCarousel();
      this.unbindAll();
      this.cleanupOrphanModals({ keepInSection: false });
    }

    get isMobileCarousel() {
      return this.mobileQuery.matches && this.dataset.enableSlider === 'true' && this.items.length > 1;
    }

    get pageCount() {
      return Math.max(1, Math.ceil(this.items.length / this.pageSize));
    }

    onBreakpointChange() {
      this.syncMobileCarousel();
    }

    onResize() {
      if (!this.isMobileCarousel) return;
      this.goToPage(this.activePage, false);
    }

    syncMobileCarousel() {
      if (this.isMobileCarousel) {
        this.enableMobileCarousel();
      } else {
        this.disableMobileCarousel();
      }
    }

    enableMobileCarousel() {
      if (!this.scroller || !this.track) return;

      this.classList.add('is-mobile-carousel');
      this.bindSwipe();

      if (this.pillsWrap) {
        const showPills = this.pageCount > 1;
        this.pillsWrap.hidden = !showPills;
        this.pillsWrap.setAttribute('aria-hidden', showPills ? 'false' : 'true');
      }

      this.goToPage(this.activePage, false);
    }

    disableMobileCarousel() {
      this.classList.remove('is-mobile-carousel');
      this.unbindSwipe();

      if (this.pillsWrap) {
        this.pillsWrap.hidden = true;
        this.pillsWrap.setAttribute('aria-hidden', 'true');
      }

      if (this.track) {
        this.track.style.transform = '';
        this.track.style.transition = '';
      }

      this.updateActivePage(0);
    }

    bindSwipe() {
      if (!this.scroller || this.mobileBound) return;

      this.scroller.addEventListener('pointerdown', this.onSwipeStart, { passive: true });
      this.scroller.addEventListener('pointermove', this.onSwipeMove, { passive: false });
      this.scroller.addEventListener('pointerup', this.onSwipeEnd, { passive: true });
      this.scroller.addEventListener('pointercancel', this.onSwipeEnd, { passive: true });
      this.mobileBound = true;
    }

    unbindSwipe() {
      if (!this.scroller || !this.mobileBound) return;

      this.scroller.removeEventListener('pointerdown', this.onSwipeStart);
      this.scroller.removeEventListener('pointermove', this.onSwipeMove);
      this.scroller.removeEventListener('pointerup', this.onSwipeEnd);
      this.scroller.removeEventListener('pointercancel', this.onSwipeEnd);
      this.mobileBound = false;
      this.swipe.active = false;
      this.swipe.locked = false;
    }

    getPageOffset(pageIndex) {
      if (!this.scroller || !this.items.length) return 0;

      const page = Math.max(0, Math.min(pageIndex, this.pageCount - 1));
      const startIndex = Math.min(page * this.pageSize, this.items.length - 1);
      const item = this.items[startIndex];
      if (!item) return 0;

      // Alinha o início da página ao item (3 por página), sem passar do fim.
      const maxOffset = Math.max(0, this.track.scrollWidth - this.scroller.clientWidth);
      return Math.min(Math.max(0, item.offsetLeft), maxOffset);
    }

    goToPage(pageIndex, animate = true) {
      const page = Math.max(0, Math.min(pageIndex, this.pageCount - 1));
      this.updateActivePage(page);

      if (!this.track || !this.scroller) return;

      const offset = this.getPageOffset(page);
      this.track.style.transition = animate ? 'transform 0.32s ease' : 'none';
      this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
    }

    updateActivePage(pageIndex) {
      const nextPage = Math.max(0, Math.min(pageIndex, this.pageCount - 1));
      this.activePage = nextPage;

      this.pills.forEach((pill, pillIndex) => {
        const isActive = pillIndex === nextPage;
        pill.classList.toggle('is-active', isActive);
        pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    onPillClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.isMobileCarousel) return;

      const pageIndex = Number(event.currentTarget.dataset.pillIndex);
      if (Number.isNaN(pageIndex)) return;
      this.goToPage(pageIndex, true);
    }

    onSwipeStart(event) {
      if (!this.isMobileCarousel) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      // Não inicia swipe a partir do botão — deixa o click abrir o modal.
      if (event.target?.closest?.('.bewo-poltrona-stories__trigger, .bewo-poltrona-stories__pill')) {
        this.swipe.active = false;
        return;
      }

      this.swipe.active = true;
      this.swipe.locked = false;
      this.swipe.startX = event.clientX;
      this.swipe.startY = event.clientY;
      this.swipe.deltaX = 0;
      this.swipe.originOffset = this.getPageOffset(this.activePage);
    }

    onSwipeMove(event) {
      if (!this.swipe.active || !this.track || !this.scroller) return;

      const dx = event.clientX - this.swipe.startX;
      const dy = event.clientY - this.swipe.startY;

      if (!this.swipe.locked) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;

        if (Math.abs(dx) > Math.abs(dy) * 1.2 && Math.abs(dx) > 12) {
          this.swipe.locked = true;
        } else {
          this.swipe.active = false;
          return;
        }
      }

      this.swipe.deltaX = dx;
      event.preventDefault();

      const base = this.swipe.originOffset || 0;
      this.track.style.transition = 'none';
      this.track.style.transform = `translate3d(${-base + dx}px, 0, 0)`;
    }

    onSwipeEnd() {
      if (!this.swipe.active) return;

      const dx = this.swipe.deltaX;
      this.swipe.active = false;
      this.swipe.locked = false;

      const threshold = Math.min(72, (this.scroller?.clientWidth || 320) * 0.18);

      if (dx <= -threshold && this.activePage < this.pageCount - 1) {
        this.goToPage(this.activePage + 1, true);
      } else if (dx >= threshold && this.activePage > 0) {
        this.goToPage(this.activePage - 1, true);
      } else {
        this.goToPage(this.activePage, true);
      }

      this.swipe.deltaX = 0;
    }

    onTriggerClick(event) {
      const trigger = event.currentTarget;

      // Se o usuário estava no meio de um swipe horizontal, não abre.
      if (this.swipe.locked || Math.abs(this.swipe.deltaX) > 24) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.openStoryModal(trigger);
    }

    get sectionId() {
      return this.dataset.sectionId || '';
    }

    cleanupOrphanModals({ keepInSection = true } = {}) {
      const sectionId = this.sectionId;
      if (!sectionId) return;

      Array.from(
        document.querySelectorAll(`basic-modal.bewo-poltrona-stories-modal[data-section-id="${sectionId}"]`)
      ).forEach((modal) => {
        const inSection = this.sectionEl ? this.sectionEl.contains(modal) : false;
        if (keepInSection && inSection) return;

        try {
          if (typeof modal.hide === 'function' && modal.open) modal.hide();
        } catch (error) {
          // Ignore.
        }

        if (!keepInSection || modal.parentElement === document.body) {
          modal.remove();
        }
      });
    }

    resolveStoryModal(modalId) {
      if (!modalId) return null;

      const section = this.sectionEl || this.closest('.shopify-section');
      const all = Array.from(document.querySelectorAll('basic-modal.bewo-poltrona-stories-modal')).filter(
        (el) => el.id === modalId
      );

      let modal = all.find((el) => section && section.contains(el));
      if (!modal) modal = all.find((el) => el.parentElement === document.body);
      if (!modal) modal = all[0] || document.getElementById(modalId);

      all.forEach((el) => {
        if (el !== modal) {
          try {
            if (typeof el.hide === 'function' && el.open) el.hide();
          } catch (error) {
            // Ignore.
          }
          el.remove();
        }
      });

      return modal || null;
    }

    openStoryModal(trigger) {
      const modalId = trigger.getAttribute('aria-controls');
      if (!modalId) return false;

      const modal = this.resolveStoryModal(modalId);
      if (!modal) return false;

      if (modal.open) {
        modal.setAttribute('active', '');
        modal.hidden = false;
        return true;
      }

      try {
        if (typeof modal.show === 'function') {
          modal.show(trigger);
        } else {
          modal.hidden = false;
          modal.setAttribute('open', '');
          modal.setAttribute('active', '');
        }
      } catch (error) {
        modal.hidden = false;
        modal.setAttribute('open', '');
        modal.setAttribute('active', '');
      }

      window.setTimeout(() => {
        if (modal.hasAttribute('open') && !modal.hasAttribute('active')) {
          modal.setAttribute('active', '');
        }
        modal.hidden = false;
      }, 80);

      return true;
    }

    closeStoryModal(trigger) {
      const modalId = trigger?.getAttribute('aria-controls');
      if (!modalId) return;
      const modal = this.resolveStoryModal(modalId);
      if (modal && typeof modal.hide === 'function' && modal.open) {
        modal.hide();
      }
    }
  }

  customElements.define('bewo-poltrona-stories', BewoPoltronaStories);
}

if (!customElements.get('bewo-poltrona-folder')) {
  class BewoPoltronaFolder extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-folder-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-folder-panel]'));
      this.onTabClick = this.onTabClick.bind(this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
      });
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
      });
    }

    onTabClick(event) {
      const tab = event.currentTarget;
      const key = tab.dataset.folderTab;
      if (!key) return;

      this.tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.dataset.activeTab = key;

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.folderPanel === key;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    }
  }

  customElements.define('bewo-poltrona-folder', BewoPoltronaFolder);
}

/* Theme editor: selecionar o bloco abre a pasta. */
(function bindPoltronaStoriesEditor() {
  if (window.__bewoPoltronaStoriesEditorBound) return;
  window.__bewoPoltronaStoriesEditorBound = true;

  const getStoryItem = (target) => {
    if (!target) return null;
    if (target.classList?.contains('bewo-poltrona-stories__item')) return target;
    return target.closest?.('.bewo-poltrona-stories__item') || target.querySelector?.('.bewo-poltrona-stories__item') || null;
  };

  const findItemByBlockId = (blockId) => {
    if (!blockId) return null;
    return (
      Array.from(document.querySelectorAll('.bewo-poltrona-stories__item')).find((item) => {
        const raw = item.getAttribute('data-shopify-editor-block') || '';
        return raw.includes(blockId);
      }) || null
    );
  };

  const openFromItem = (item) => {
    if (!item) return;
    const root = item.closest('bewo-poltrona-stories');
    const trigger = item.querySelector('.bewo-poltrona-stories__trigger');
    if (!root || !trigger || typeof root.openStoryModal !== 'function') return;

    const open = () => root.openStoryModal(trigger);
    requestAnimationFrame(open);
    window.setTimeout(open, 80);
  };

  document.addEventListener('shopify:block:select', (event) => {
    let item = getStoryItem(event.target);
    if (!item) item = findItemByBlockId(event.detail?.blockId);
    openFromItem(item);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    let item = getStoryItem(event.target);
    if (!item) item = findItemByBlockId(event.detail?.blockId);
    if (!item) return;

    const root = item.closest('bewo-poltrona-stories');
    const trigger = item.querySelector('.bewo-poltrona-stories__trigger');
    if (!root || !trigger || typeof root.closeStoryModal !== 'function') return;
    root.closeStoryModal(trigger);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('bewo-poltrona-stories');
    if (!root || typeof root.connectedCallback !== 'function') return;
    root._storiesBound = false;
    root.connectedCallback();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    document
      .querySelectorAll(`basic-modal.bewo-poltrona-stories-modal[data-section-id="${sectionId}"]`)
      .forEach((modal) => {
        try {
          if (typeof modal.hide === 'function' && modal.open) modal.hide();
        } catch (error) {
          // Ignore.
        }
        modal.remove();
      });
  });
})();
